// prototypes/BudgetKpiGrid.tsx (v2.18)
// 「予算・前年比較」 タブ 1段目: 20指標 (4列×5行) の KPI cards。
// KpiCards と同じ見た目 (当月実績 / 予算 / 予算比 / 前年比 / 前月比) を 20 spec で描画。
// data は親 (PrototypePanel) から渡された DeptMetricsAllResponse を resolveAllSpecs() で 20 ResolvedMetric に変換済み。
import { useMemo } from "react";
import { fmtCardMain, fmtYenScaled, isYenCardMetric, yenUseOku, fmtRatio, fmtRatioPct } from "../../lib/format";
import type { Direction } from "@shared/api";
import type { ResolvedMetric } from "./budgetSpec";

interface Props { resolved: ResolvedMetric[]; targetName: string; ymLabel: string; avgMode?: boolean; }

export function BudgetKpiGrid({ resolved, targetName, ymLabel, avgMode = false }: Props) {
  // Cards は tableOnly を除外して 18 表示 (6×3 grid)
  const cardRows = resolved.filter((r) => !r.spec.tableOnly);
  return (
    <section>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h2 className="text-base font-semibold">{targetName} 主要指標({avgMode ? "直近12ヶ月平均" : ymLabel})</h2>
        <span className="text-[11px] text-slate-500 flex items-center gap-1">
          <span className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-[9px]">A</span>予算達成
          <span className="w-4 h-4 rounded-full bg-amber-400 text-white flex items-center justify-center font-bold text-[9px]">B</span>前年超
          <span className="w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center font-bold text-[9px]">C</span>それ以外
        </span>
      </div>
      {/* 2 列 (mobile) → 3 列 (md) → 6 列 (lg+) で 3 行 (FHD 想定) /
          print-6cols: 印刷時は viewport 幅に関わらず 6 列を強制 (A4 横) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 print-6cols">
        {cardRows.map((r) => (
          <BudgetKpiCard key={r.spec.key} r={r} avgMode={avgMode} />
        ))}
      </div>
    </section>
  );
}

// GP / GP単価 / 出来高換算薬剤費 / 出来高換算材料費 + これらに由来する派生指標 (出来高換算薬剤材料費 / GP単価_在院) は 月末未確定月に 0 になる仕様
// → 0 を「未確定」 として「—」 扱い (KpiCards と同じ判定)
const SKIP_ZERO_KEYS = new Set([
  "GP", "GP単価", "GP単価_在院",
  "出来高換算薬剤費", "出来高換算材料費", "出来高換算薬剤材料費",
]);

function BudgetKpiCard({ r, avgMode }: { r: ResolvedMetric; avgMode: boolean }) {
  const { spec } = r;
  const skipZero = SKIP_ZERO_KEYS.has(spec.key);
  const adj = (v: number | null): number | null => {
    if (v == null) return null;
    if (skipZero && v === 0) return null;
    return v;
  };
  // 直近12ヶ月平均モード: 末尾12ヶ月の単純平均で 主数値・予算・前年 を置換 (前月比は非表示)。
  const last12 = r.trend.slice(-12);
  const mean12 = (sel: (p: typeof r.trend[number]) => number | null): number | null => {
    const vals = last12.map((p) => adj(sel(p))).filter((v): v is number => v != null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };
  const cur = avgMode ? mean12((p) => p.actual)   : adj(r.current);
  const bud = avgMode ? mean12((p) => p.budget)   : adj(r.budget);
  const py  = avgMode ? mean12((p) => p.prevYear) : adj(r.prevYear);
  const pm  = adj(r.prevMonth);

  // 入院収入 / 粗利(GP) カードのみ 円 単位表示 (千円→円、 億円/万円に自動スケール、 単位を数値に併記)。 表は千円のまま。
  const isYen = isYenCardMetric(spec.key, spec.unit);
  // 億/万 は「期間内(trend)の最大実績」で判定 (カード/マトリックス共通基準、 ユーザ指示 2026-06-24)。
  const yenOku = isYen && yenUseOku(Math.max(0, ...r.trend.map((p) => Math.abs(p.actual ?? 0))));
  const cm = (v: number | null | undefined) => isYen ? fmtYenScaled(v, yenOku) : fmtCardMain(v, spec.unit, spec.key);
  const main = cm(cur);
  // 比較値(予算/前年/前月)の括弧内表示。 円カードは単位も併記。
  const fc = (v: number | null) => {
    const f = cm(v);
    return isYen && f.value !== "—" ? `${f.value} ${f.unit}` : f.value;
  };
  const ratio = (cur != null && bud != null && bud !== 0) ? cur / bud : null;
  const yoyDelta = fmtRatioPct(cur, py);
  const momDelta = fmtRatioPct(cur, pm);

  const ratioBadge = useMemo(() => makeRatioBadge(ratio, spec.direction), [ratio, spec.direction]);
  const yoyBadge   = useMemo(() => makeYoyBadge(cur, py, yoyDelta, spec.direction), [cur, py, yoyDelta, spec.direction]);
  const grade = gradeOf(cur, bud, py, spec.direction);

  return (
    <div className="kpi-card relative">
      {grade && (
        <span className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center text-sm font-extrabold ${gradeStyle(grade)}`}
              title="予算達成=A / 予算未達でも前年超=B / それ以外=C">{grade}</span>
      )}
      <div className="flex items-center mb-1">
        <div className="kpi-label flex items-center gap-1 pr-6">
          {spec.label}
          {!isYen && main.unit && <span className="text-slate-400 font-normal">({main.unit})</span>}
          {spec.description && (
            <span title={spec.description} className="text-slate-400 cursor-help text-[10px] hover:text-slate-600">ⓘ</span>
          )}
        </div>
      </div>
      <div className="flex items-baseline">
        <div className="kpi-value">{main.value}{isYen && main.value !== "—" && <span className="text-sm font-bold text-slate-400 ml-1">{main.unit}</span>}</div>
      </div>
      {/* 各比率の右に () で比較対象の絶対値を併記 — 例: 予算比 104% (1,411,980)
          3 行とも text-xs + font-semibold + leading-tight で統一、 色のみ direction で differ。 */}
      <div className={`text-xs font-semibold mt-0.5 leading-tight ${ratioBadge.color}`}>
        予算比 {ratioBadge.label} <span className="text-slate-400 font-normal">({fc(bud)})</span>
      </div>
      <div className={`text-xs font-semibold mt-0.5 leading-tight ${yoyBadge.color}`}>
        前年比 {yoyBadge.label} <span className="text-slate-400 font-normal">({fc(py)})</span>
      </div>
      {!avgMode && (
        <div className="text-xs font-semibold mt-0.5 leading-tight text-slate-500">
          前月比 {momDelta} <span className="text-slate-400 font-normal">({fc(pm)})</span>
        </div>
      )}
    </div>
  );
}

// 2026-06-15: 予算比・前年比の色分けは 達成=青 / 未達=赤 の 2 色だけ (シンプル化)。
function makeRatioBadge(ratio: number | null, direction: Direction) {
  if (ratio == null) return { color: "text-slate-400", label: "—" };
  const label = fmtRatio(ratio);
  if (direction === "neutral") return { color: "text-slate-600", label };
  const ok = direction === "lower_is_better" ? ratio <= 1 : ratio >= 1;
  return { color: ok ? "text-blue-700" : "text-red-600", label };
}

function makeYoyBadge(cur: number | null, py: number | null, yoyDelta: string, direction: Direction) {
  if (cur == null || py == null || py === 0) return { color: "text-slate-400", label: "—" };
  if (direction === "neutral") return { color: "text-slate-600", label: yoyDelta };
  const ok = direction === "lower_is_better" ? cur <= py : cur >= py;
  return { color: ok ? "text-blue-700" : "text-red-600", label: yoyDelta };
}

// 達成度 A/B/C (2026-06-15 定義): 予算達成=A / 予算未達でも前年超=B / それ以外=C
// (lower_is_better は下回りが達成)。 評価できる基準が無ければ null。
function gradeOf(cur: number | null, budget: number | null, prevYear: number | null, direction: Direction): "A" | "B" | "C" | null {
  if (cur == null || direction === "neutral") return null;
  const beats = (ref: number | null): boolean | null =>
    ref == null ? null : (direction === "lower_is_better" ? cur <= ref : cur >= ref);
  const budOk = beats(budget), pyOk = beats(prevYear);
  if (budOk === true) return "A";
  if (pyOk === true) return "B";
  if (budOk == null && pyOk == null) return null;
  return "C";
}
function gradeStyle(g: "A" | "B" | "C"): string {
  return g === "A" ? "bg-blue-600 text-white" : g === "B" ? "bg-amber-400 text-white" : "bg-red-500 text-white";
}
